/**
 * Client-side helpers لتسجيل/إلغاء اشتراك Web Push.
 * يستدعي serverFn لحفظ/حذف الاشتراك في DB.
 */
import { VAPID_PUBLIC_KEY, urlBase64ToUint8Array } from "./vapid-public";
import { isPushAllowedContext, isIosNeedsInstall } from "./guard";
import { saveWebPushSubscription, deleteWebPushSubscription } from "./webPush.functions";
import {
  saveAnonWebPushSubscription,
  deleteAnonWebPushSubscription,
} from "./webPushAnon.functions";
import { getSessionId, getVisitorId } from "@/lib/activityTracker";

const SW_PATH = "/push-sw.js";

async function getOrRegisterSW(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (existing) return existing;
  return await navigator.serviceWorker.register(SW_PATH, { scope: "/" });
}

export type PushEnableResult =
  | { ok: true }
  | {
      ok: false;
      reason: "context" | "ios-install" | "denied" | "blocked" | "error";
      message?: string;
    };

export async function enablePush(
  locale: "ar" | "en" = "ar",
  opts?: { market?: string },
): Promise<PushEnableResult> {
  // iOS بلا تثبيت على الشاشة الرئيسيّة لا يمنح إذن الإشعارات أبداً
  if (isIosNeedsInstall()) return { ok: false, reason: "ios-install" };
  if (!isPushAllowedContext()) return { ok: false, reason: "context" };

  try {
    // إذن محجوب مسبقاً → لا فائدة من requestPermission (يعيد denied صامتاً)
    if (Notification.permission === "denied") return { ok: false, reason: "blocked" };
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return { ok: false, reason: "denied" };

    const reg = await getOrRegisterSW();
    await navigator.serviceWorker.ready;

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const key = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: key.buffer.slice(key.byteOffset, key.byteOffset + key.byteLength) as ArrayBuffer,
      });
    }

    const json = sub.toJSON() as {
      endpoint: string;
      keys: { p256dh: string; auth: string };
    };

    // المسجَّل → الدالّة المحميّة (تربط user_id) · المجهول → الدالّة العامّة (visitor_id)
    // ⚠️ لا supabase.auth.getSession() هنا (سباق navigatorLock) — نستعمل snapshot
    const { getAuthSnapshot } = await import("@/lib/auth-snapshot");
    if (getAuthSnapshot().accessToken) {
      await saveWebPushSubscription({
        data: {
          endpoint: json.endpoint,
          p256dh: json.keys.p256dh,
          auth: json.keys.auth,
          locale,
          userAgent: navigator.userAgent.slice(0, 500),
        },
      });
    } else {
      await saveAnonWebPushSubscription({
        data: {
          endpoint: json.endpoint,
          p256dh: json.keys.p256dh,
          auth: json.keys.auth,
          locale,
          market: opts?.market,
          sessionId: getSessionId() || undefined,
          visitorId: getVisitorId() || undefined,
          userAgent: navigator.userAgent.slice(0, 500),
        },
      });
    }


    return { ok: true };
  } catch (e: any) {
    return { ok: false, reason: "error", message: e?.message ?? "unknown" };
  }
}

export async function disablePush(): Promise<boolean> {
  if (!isPushAllowedContext()) return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (!reg) return true;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const { getAuthSnapshot } = await import("@/lib/auth-snapshot");
      if (getAuthSnapshot().accessToken) {
        await deleteWebPushSubscription({ data: { endpoint: sub.endpoint } });
      } else {
        await deleteAnonWebPushSubscription({ data: { endpoint: sub.endpoint } });
      }
      await sub.unsubscribe();
    }
    return true;
  } catch {
    return false;
  }
}

export async function isPushEnabled(): Promise<boolean> {
  if (!isPushAllowedContext()) return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return !!sub && Notification.permission === "granted";
  } catch {
    return false;
  }
}
